/**
 * Regras puras do Mural de Insights (Figma, quadro 4).
 */

import { t } from './textos.js';

/** `RN-INS-02`: as cinco cores configuradas (HEX do Figma) e a cor da dobra. */
export const CORES_NOTA = Object.freeze({
    amarelo: { cor: 0xffeb48, dobra: 0xd0c145 },
    azul: { cor: 0xbedeff, dobra: 0x4489d0 },
    verde: { cor: 0xbdfaa7, dobra: 0x7ab460 },
    laranja: { cor: 0xffb042, dobra: 0xb47b2d },
    rosa: { cor: 0xf3bdd5, dobra: 0xcf6393 }
});

const NOMES = Object.keys(CORES_NOTA);

/**
 * `RN-INS-02` + `RN-INS-03`: sorteia a cor da nova nota; uma cor só volta
 * depois de pelo menos duas notas de outras cores.
 *
 * @param {string[]} coresAnteriores cores já geradas, da mais antiga à mais nova
 * @param {() => number} aleatorio fonte em [0, 1)
 */
export function sortearCor (coresAnteriores = [], aleatorio = Math.random) {
    const bloqueadas = new Set(coresAnteriores.slice(-2));
    const livres = NOMES.filter((nome) => !bloqueadas.has(nome));
    return livres[Math.min(livres.length - 1, Math.floor(aleatorio() * livres.length))];
}

/** Garante uma cor válida em toda nota (o backend não guarda a cor). */
export function completarCores (notas, coresSalvas = {}, aleatorio = Math.random) {
    const historico = [];
    return notas.map((nota) => {
        const salva = nota.cor ?? coresSalvas[nota.id];
        const cor = CORES_NOTA[salva] ? salva : sortearCor(historico, aleatorio);
        historico.push(cor);
        return { ...nota, cor };
    });
}

/** "26/10 • 08:05", no horário do tablet. */
export function formatarDataNota (iso) {
    const data = new Date(iso);
    if (Number.isNaN(data.getTime())) return '';
    const dois = (n) => String(n).padStart(2, '0');
    return `${dois(data.getDate())}/${dois(data.getMonth() + 1)} • ${dois(data.getHours())}:${dois(data.getMinutes())}`;
}

/** Teto do texto da nota no tablet; o servidor aceita até 300 (`LC-INT-006`). */
export const TETO_NOTA = 280;

/** `RN-INT-015`: a classe da falha vem do `status` do `ApiErro`, nunca do `codigo` nem do texto. */
export function classificarFalhaMural (falha) {
    switch (falha?.status) {
        case 401: return 'sessao_invalida';
        case 403: return 'sem_grupo_ou_turma';
        case 422: return 'texto_invalido';
        default: return 'transitoria';
    }
}

/** Texto exibido no editor para cada classe de falha (conteúdo exato: DP-INT-017). */
export function mensagemDaFalhaMural (classe, operacao = 'salvar') {
    if (classe === 'sessao_invalida') return 'Sua sessão não é mais válida. Avise o facilitador.';
    if (classe === 'sem_grupo_ou_turma') return 'Seu usuário não está vinculado a um grupo. Avise o facilitador.';
    if (classe === 'texto_invalido') return 'O texto da nota não foi aceito. Revise e edite a nota.';
    // As duas mensagens genéricas vêm do catálogo de textos, editáveis pelo painel.
    return operacao === 'excluir' ? t('mural.erro_excluir') : t('mural.erro_salvar');
}

const semResposta = (falha) => typeof falha?.status !== 'number';
const RECUSAS_DEFINITIVAS = new Set(['sessao_invalida', 'sem_grupo_ou_turma', 'texto_invalido']);

/**
 * Lógica de gravação do Mural fora da cena (`RN-INT-013`).
 * - nota nova que falha continua no mural, com o texto digitado;
 * - 401/403/422 não reenviam o mesmo texto;
 * - POST sem resposta: a próxima gravação relista (`GET`) antes de recriar (`RN-INT-016`).
 */
export function criarGravadorMural ({ fonte, notas = [] }) {
    const estados = new WeakMap();
    const estadoDe = (nota) => {
        if (!estados.has(nota)) estados.set(nota, { recusa: null, perdido: null });
        return estados.get(nota);
    };
    const resultadoDeFalha = (falha, operacao) => {
        const classe = classificarFalhaMural(falha);
        return { estado: 'falhou', classe, mensagem: mensagemDaFalhaMural(classe, operacao) };
    };

    const incluirNoMural = (nota) => {
        if (!notas.includes(nota)) notas.push(nota);
    };

    /** Relista e procura a nota do POST perdido: id que a tela não conhece e o mesmo texto. */
    const buscarNotaDoPostPerdido = async (textoEnviado) => {
        const lista = await fonte.listarInsights();
        const conhecidos = new Set(notas.map((n) => n.id).filter(Boolean));
        return lista.find((i) => !conhecidos.has(i.id) && i.texto === textoEnviado);
    };

    const adotar = (nota, achada) => {
        const { id, texto, criadoEm, atualizadoEm } = achada;
        Object.assign(nota, { id, texto, criadoEm, atualizadoEm });
    };

    return {
        get notas () { return notas; },

        nova ({ cor }) {
            return { id: null, texto: '', criadoEm: null, cor };
        },

        async salvar (nota, textoBruto) {
            const texto = String(textoBruto ?? '').trim();
            if (!temConteudo(texto) || (nota.id && texto === nota.texto)) return { estado: 'inalterada' };

            const estado = estadoDe(nota);
            if (estado.recusa?.texto === texto) {
                const classe = estado.recusa.classe;
                return { estado: 'falhou', classe, mensagem: mensagemDaFalhaMural(classe, 'salvar') };
            }

            let chamouPost = false;
            const criar = () => {
                chamouPost = true;
                return fonte.criarInsight({ texto, cor: nota.cor });
            };

            try {
                let salva;
                if (nota.id) {
                    salva = await fonte.atualizarInsight(nota.id, { texto });
                } else if (estado.perdido) {
                    const achada = await buscarNotaDoPostPerdido(estado.perdido.texto);
                    estado.perdido = null;
                    if (achada) {
                        adotar(nota, achada);
                        salva = achada.texto === texto ? achada : await fonte.atualizarInsight(achada.id, { texto });
                    } else {
                        salva = await criar();
                    }
                } else {
                    salva = await criar();
                }
                estado.recusa = null;
                estado.perdido = null;
                Object.assign(nota, salva, { cor: nota.cor ?? salva.cor });
                incluirNoMural(nota);
                return { estado: 'gravada' };
            } catch (falha) {
                const resultado = resultadoDeFalha(falha, 'salvar');
                estado.recusa = RECUSAS_DEFINITIVAS.has(resultado.classe) ? { texto, classe: resultado.classe } : null;
                // Só o POST sem resposta pode ter gravado no servidor: marca para relistar.
                if (chamouPost && semResposta(falha)) estado.perdido = { texto };
                if (!nota.id) {
                    nota.texto = texto;      // a nota pendente continua visível, com o texto digitado
                    incluirNoMural(nota);
                }
                return resultado;
            }
        },

        async excluir (nota) {
            const estado = estadoDe(nota);
            try {
                if (nota.id) {
                    await fonte.excluirInsight(nota.id);
                } else if (estado.perdido) {
                    // `RN-INT-017`: o POST perdido pode ter gravado; relista e apaga no servidor.
                    const achada = await buscarNotaDoPostPerdido(estado.perdido.texto);
                    if (achada) await fonte.excluirInsight(achada.id);
                    estado.perdido = null;   // só depois do sucesso/ausência: falha segue retentável
                }
            } catch (falha) {
                return resultadoDeFalha(falha, 'excluir');
            }
            const indice = notas.indexOf(nota);
            if (indice >= 0) notas.splice(indice, 1);
            return { estado: 'excluida' };
        }
    };
}

/** `RI-INS-05`: a lixeira só aparece quando a nota tem conteúdo. */
export function temConteudo (texto) {
    return String(texto ?? '').trim().length > 0;
}

/**
 * Escreve `texto` num Text do Phaser em no máximo `maximo` linhas, com
 * reticências na sobra. As reticências podem empurrar a última palavra para
 * uma linha nova: encurta até caber de fato.
 */
export function escreverResumido (objeto, texto, maximo = 5) {
    let linhas = objeto.getWrappedText(texto);
    if (linhas.length <= maximo) return objeto.setText(texto);
    let resumo = resumirLinhas(linhas, maximo);
    for (let i = 0; i < 12 && objeto.getWrappedText(resumo).length > maximo; i++) {
        resumo = `${resumo.slice(0, -4).trimEnd()}...`;
    }
    return objeto.setText(resumo);
}

/** Corta em `maximo` linhas e marca a sobra com reticências (cartão do mural). */
export function resumirLinhas (linhas, maximo = 5) {
    if (linhas.length <= maximo) return linhas.join('\n');
    const visiveis = linhas.slice(0, maximo);
    visiveis[maximo - 1] = `${visiveis[maximo - 1].replace(/[\s.,;:!?]+$/, '').slice(0, -1).trimEnd()}...`;
    return visiveis.join('\n');
}
